import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    makeStyles,
    Theme,
    createStyles,
    TableFooter,
} from '@material-ui/core';
import i18n from 'i18n-js';
import { PaymentMethodBreakdownDto } from 'api/models';
import { NothingFoundMessage } from 'components/shared';
import { ShareProgressBar } from './shareProgressBar';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        progressContainer: {
            padding: '2px 0px 6px 0px',
            marginLeft: 24,
        },
        textAccent: {
            fontWeight: 600,
            fontSize: 13,
            color: theme.palette.primary.dark,
        },
        footerText: {
            fontWeight: 700,
            fontSize: 13,
            color: theme.palette.primary.dark,
        },
        textUpper: {
            textTransform: 'uppercase',
        },
    })
);

function formatPrice(amount: number, currency: string): string {
    return `${currency} ${amount.toFixed(2)}`;
}

interface Props {
    data: PaymentMethodBreakdownDto[];
    currency: string;
}

export default function PaymentReportTable({ data, currency }: Props) {
    const classes = useStyles();
    const totalAmount = data.reduce((acc, next) => (acc += next.amount), 0);
    const totalTransactionCount = data.reduce((acc, next) => (acc += next.transactionCount), 0);
    const totalTips = data.reduce((acc, next) => (acc += next.tipsAmount), 0);

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{i18n.t('finances.paymentMethod')}</TableCell>
                        <TableCell align="right">{i18n.t('finances.transactions')}</TableCell>
                        <TableCell align="right">
                            {i18n.t('common.amount')} ({currency})
                        </TableCell>
                        <TableCell align="right" width={200}>
                            {i18n.t('finances.share')}
                        </TableCell>
                        <TableCell align="right">{i18n.t('finances.avgPerTransaction')}</TableCell>
                        <TableCell align="right">
                            {i18n.t('finances.tips')} ({currency})
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {data.length > 0 &&
                        data.map((row, i) => {
                            return (
                                <TableRow key={i}>
                                    {/* Method */}
                                    <TableCell>
                                        <Typography variant="body2">
                                            {i18n.t(
                                                `finances.paymentMethodEnum.${row.method.toLowerCase()}`
                                            )}
                                        </Typography>
                                    </TableCell>

                                    {/* Transactions */}
                                    <TableCell align="right">{row.transactionCount}</TableCell>

                                    {/* Amount */}
                                    <TableCell align="right" className={classes.footerText}>
                                        {formatPrice(row.amount, currency)}
                                    </TableCell>

                                    {/* Share */}
                                    <TableCell align="right">
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="end"
                                            className={classes.progressContainer}
                                        >
                                            <Typography variant="caption">
                                                {row.sharePercent < 0.01 ? '<0.01' : row.sharePercent}
                                                %
                                            </Typography>
                                            <ShareProgressBar
                                                variant="determinate"
                                                value={row.sharePercent}
                                                style={{ width: '100%' }}
                                            />
                                        </Box>
                                    </TableCell>

                                    {/* Avg per transaction */}
                                    <TableCell align="right">
                                        {formatPrice(row.avgPerTransaction, currency)}
                                    </TableCell>

                                    {/* Tips */}
                                    <TableCell align="right">
                                        {row.tipsAmount > 0
                                            ? formatPrice(row.tipsAmount, currency)
                                            : '--'}
                                    </TableCell>
                                </TableRow>
                            );
                        })}

                    {data.length === 0 && (
                        <TableRow>
                            <TableCell height={500} colSpan={100}>
                                <NothingFoundMessage />
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>

                {data.length > 0 && (
                    <TableFooter>
                        <TableRow>
                            <TableCell className={`${classes.footerText} ${classes.textUpper}`}>
                                {i18n.t('common.total')}
                            </TableCell>
                            <TableCell className={classes.footerText} align="right">
                                {totalTransactionCount}
                            </TableCell>
                            <TableCell className={classes.footerText} align="right">
                                {formatPrice(totalAmount, currency)}
                            </TableCell>
                            <TableCell className={classes.footerText} align="right">
                                100%
                            </TableCell>
                            <TableCell className={classes.footerText} align="right">
                                {formatPrice(totalAmount / totalTransactionCount, currency)}
                            </TableCell>
                            <TableCell className={classes.footerText} align="right">
                                {formatPrice(totalTips, currency)}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                )}
            </Table>
        </TableContainer>
    );
}
